import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Check } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import { MEDIA } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Community Knowledge Systems",
  "Cultural Governance",
  "Language Revitalisation",
  "Ecological Restoration",
  "SeedKeepers",
  "Stewardship Intelligence",
  "Partnership / Philanthropy",
  "Something else",
];

const ROLES = [
  "Elder", "Traditional Owner", "Archivist", "Ranger", "Educator",
  "Scientist", "Artist", "Community Leader", "Other",
];

const empty = { name: "", email: "", organisation: "", role: "", interest: "", message: "" };

export default function Partner() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Thank you. Your message has been received with care.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/15 focus:border-[#D4A373] outline-none py-3 text-[#F4F1ED] placeholder:text-[#A8A5A1]/50 transition-colors duration-300";
  const selectCls = `${inputCls} appearance-none cursor-pointer`;

  return (
    <div data-testid="partner-page" className="pt-20">
      {/* header */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <img src={MEDIA.closing} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/60 via-[#050806]/85 to-[#050806]" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Label className="block mb-6">Become a Wisdom Keeper</Label>
            <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED]">
              Start a Conversation
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#A8A5A1] leading-relaxed">
              Whether you are an Elder, Traditional Owner, archivist, ranger, educator,
              scientist, artist or community leader — there is a place for you in this work.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:gap-8">
              <a
                href="mailto:william@wisdomkeepers.com"
                data-testid="partner-email-link"
                className="text-[#D4A373] hover:text-[#E6CCB2] transition-colors duration-300"
              >
                william@wisdomkeepers.com
              </a>
              <a
                href="tel:+61411798772"
                data-testid="partner-phone-link"
                className="text-[#D4A373] hover:text-[#E6CCB2] transition-colors duration-300"
              >
                +614 117 98 772
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* form */}
      <section className="pb-28 md:pb-40">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {done ? (
            <Reveal>
              <div
                data-testid="contact-success"
                className="rounded-2xl border border-[#D4A373]/40 bg-[#101612] p-12 text-center"
              >
                <div className="mx-auto h-14 w-14 rounded-full border border-[#D4A373] flex items-center justify-center">
                  <Check className="text-[#D4A373]" size={26} />
                </div>
                <h2 className="mt-6 font-display text-3xl text-[#F4F1ED]">
                  Your message has been received.
                </h2>
                <p className="mt-4 text-[#A8A5A1]">
                  We steward every conversation with care and will respond thoughtfully.
                </p>
                <button
                  data-testid="contact-reset-button"
                  onClick={() => setDone(false)}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-[#F4F1ED] hover:border-[#D4A373] transition-colors duration-500"
                >
                  Send another message
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form data-testid="contact-form" onSubmit={submit} className="space-y-10">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="wk-label block mb-3">Your name *</label>
                    <input
                      data-testid="contact-name-input"
                      className={inputCls}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="wk-label block mb-3">Email *</label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="wk-label block mb-3">Organisation / Community</label>
                    <input
                      data-testid="contact-org-input"
                      className={inputCls}
                      value={form.organisation}
                      onChange={update("organisation")}
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="wk-label block mb-3">You are a…</label>
                    <select
                      data-testid="contact-role-select"
                      className={selectCls}
                      value={form.role}
                      onChange={update("role")}
                    >
                      <option value="" className="bg-[#101612]">Select a role</option>
                      {ROLES.map((r) => (
                        <option key={r} value={r} className="bg-[#101612]">{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="wk-label block mb-3">What brings you here?</label>
                  <select
                    data-testid="contact-interest-select"
                    className={selectCls}
                    value={form.interest}
                    onChange={update("interest")}
                  >
                    <option value="" className="bg-[#101612]">Select an area of interest</option>
                    {INTERESTS.map((it) => (
                      <option key={it} value={it} className="bg-[#101612]">{it}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="wk-label block mb-3">Your message *</label>
                  <textarea
                    data-testid="contact-message-input"
                    rows={5}
                    className={`${inputCls} resize-none`}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about the knowledge you carry, or the work you hope to do together."
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="group inline-flex items-center justify-center gap-2 px-9 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500 disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Start a Conversation"}
                  <Send size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
